import { difference, union } from 'lodash-es';

import { ChangesStatus } from '@editor2/types/changesStatus';
import { type Conflict, type ReorderConflict } from '@editor2/types/conflict';
import { type CourseState } from '@editor2/types/courseState';
import { NgTraditional } from '@editor2/types/ngTraditional';
import {
	getChapterByPageGroupId,
	getParentEntityByPageId
} from '@editor2/components/CourseChangesDiff/helpers';
import {
	checkChapterPagesDuplicates,
	checkPageGroupDuplicates,
	getIncomingParent
} from '@editor2/services/conflictResolutionService';

import {
	handleDeletedInAcceptCurrentReorder,
	handleDeletedInAcceptIncomingPageGroupsReorder,
	handleDeletedInAcceptIncomingPagesReorder,
	handleNewInAcceptCurrentReorder,
	handleNewInAcceptIncomingPageGroupsReorder,
	handleNewInAcceptIncomingPagesReorder
} from '../common/conflictResolutionHelper';

export class ChapterChildrenReorderConflictResolver {
	acceptCurrent = (state: CourseState, conflict: Conflict | ReorderConflict) => {
		const { entityId, attribute, initial, current, incoming } = conflict as ReorderConflict;
		const deletedOrMovedTo = difference(initial, incoming);
		const addedOrMovedFrom = difference(incoming, current);

		if (state.chapters.entities[entityId].page_groups_enabled) {
			state.initialCourse.chapters.entities[entityId].page_groups = incoming;
			state.chapters.entities[entityId].page_groups = current;

			const childEntityType = NgTraditional.PageGroup;

			const newPayload = { state, addedOrMovedFrom, childEntityType };
			handleNewInAcceptCurrentReorder(newPayload);

			const deletedPayload = { state, deletedOrMovedTo, childEntityType, attribute };
			handleDeletedInAcceptCurrentReorder(deletedPayload);

			checkPageGroupDuplicates(state, entityId);
		} else {
			state.initialCourse.chapters.entities[entityId].pages = incoming;
			state.chapters.entities[entityId].pages = current;

			const childEntityType = NgTraditional.Page;

			const newPayload = { state, addedOrMovedFrom, childEntityType };
			handleNewInAcceptCurrentReorder(newPayload);

			const deletedPayload = { state, deletedOrMovedTo, childEntityType, attribute };
			handleDeletedInAcceptCurrentReorder(deletedPayload);

			checkChapterPagesDuplicates(state, entityId);
		}
	};

	acceptIncoming = (state: CourseState, conflict: Conflict | ReorderConflict) => {
		// 1 - discard current changes
		// 2 - apply incoming changes

		const { entityId, initial, current, incoming } = conflict as ReorderConflict;
		const deletedOrMovedTo = difference(initial, current);
		const addedOrMovedFrom = difference(current, initial);

		if (state.chapters.entities[entityId].page_groups_enabled) {
			handleNewInAcceptIncomingPageGroupsReorder({ state, addedOrMovedFrom });
			handleDeletedInAcceptIncomingPageGroupsReorder({ state, deletedOrMovedTo });

			state.initialCourse.chapters.entities[entityId].page_groups = incoming;
			state.chapters.entities[entityId].page_groups = incoming;

			checkPageGroupDuplicates(state, entityId);
		} else {
			handleNewInAcceptIncomingPagesReorder({ state, addedOrMovedFrom });
			handleDeletedInAcceptIncomingPagesReorder({ state, deletedOrMovedTo });

			state.initialCourse.chapters.entities[entityId].pages = incoming;
			state.chapters.entities[entityId].pages = incoming;

			checkChapterPagesDuplicates(state, entityId);
		}
	};

	acceptBoth = (state: CourseState, conflict: Conflict | ReorderConflict) => {
		const { entityId, attribute, initial, current, incoming } = conflict as ReorderConflict;
		const deletedCurrent = difference(initial, current);
		const deletedIncoming = difference(initial, incoming);
		const merged = union(current, incoming);

		if (state.chapters.entities[entityId].page_groups_enabled) {
			deletedCurrent.map((pageGroupId) => {
				const currentParent = getChapterByPageGroupId(state.chapters, pageGroupId);
				if (!currentParent) {
					state.page_groups.entities[pageGroupId] =
						state.initialCourse.page_groups.entities[pageGroupId];
					state.changes.page_groups[pageGroupId] = [];
				}
			});
			deletedIncoming.map((pageGroupId) => {
				const incomingParent = getIncomingParent({ attribute, id: pageGroupId, state });
				if (!incomingParent) {
					state.changes.page_groups[pageGroupId] = [ChangesStatus.New];
				}
			});

			state.initialCourse.chapters.entities[entityId].page_groups = incoming;
			state.chapters.entities[entityId].page_groups = merged;

			checkPageGroupDuplicates(state, entityId);
		} else {
			deletedCurrent.map((pageId) => {
				const currentParent =
					getParentEntityByPageId(state.chapters, pageId) ||
					getParentEntityByPageId(state.page_groups, pageId);
				if (!currentParent) {
					state.pages[pageId] = state.initialCourse.pages.entities[pageId];
					state.changes.pages[pageId] = [];
				}
			});
			deletedIncoming.map((pageId) => {
				const incomingParent = getIncomingParent({ attribute, id: pageId, state });
				if (!incomingParent) {
					state.changes.pages[pageId] = [ChangesStatus.New];
				}
			});

			state.initialCourse.chapters.entities[entityId].pages = incoming;
			state.chapters.entities[entityId].pages = merged;

			checkChapterPagesDuplicates(state, entityId);
		}
	};
}
